import { Button, useDisclosure } from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { FaPlus, FaTrash } from "react-icons/fa6";
import { api } from "@/api";
import { AccessCard } from "@/api/accessCards/accessCards";
import useErrorHandling from "@/hooks/useErrorHandling";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { skudLinks, Links } from "@/app/components/navigation/links/Links";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { setSelectedLock } from "@/app/store/locks/reducer";
import { NewLockHandbook } from "@/app/components/handbooks/lockHandbook";
import { CreateAccessCardModal } from "@/app/components/modals/accessCards/createAccessCardModal";
import { UserChip } from "@/app/components/chips/userChip";

export function SkudPage() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const selectedLockId = useAppSelector((state) => state.locks.selectedLockId);

  const [accessCards, setAccessCards] = useState<AccessCard[]>([]);
  const handleError = useErrorHandling();

  const createAccessCardModalDisclosure = useDisclosure();

  const handleUnlock = useCallback(() => {
    if (!selectedLockId) {
      toast.error("Выберите замок");
      return;
    }

    api.locks
      .unlock(selectedLockId)
      .then(() => toast.success("Замок успешно открыт!"))
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [selectedLockId, handleError]);

  const loadAccessCards = useCallback(() => {
    api.accessCards
      .list()
      .then((response) => {
        setAccessCards(response.data);
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [handleError]);

  useEffect(() => {
    loadAccessCards();
  }, [loadAccessCards, createAccessCardModalDisclosure.isOpen]);

  const handleDelete = useCallback(
    (cardId: number) => {
      if (!confirm("Вы уверены, что хотите удалить эту карту доступа?")) {
        return;
      }

      api.accessCards
        .delete(cardId)
        .then(() => {
          toast.success("Карта доступа успешно удалена");
          loadAccessCards();
        })
        .catch((err) => {
          console.log(err);
          const { errorMessage } = handleError(err);
          toast.error(errorMessage);
        });
    },
    [loadAccessCards],
  );

  return (
    <>
      <CreateAccessCardModal
        disclosure={createAccessCardModalDisclosure}
        onSuccess={loadAccessCards}
      />
      <div className="flex flex-col md:flex-row gap-4 w-full">
        <div className="flex flex-col gap-2 w-full md:min-w-[240px] md:w-auto">
          <NewLockHandbook
            errorMessage=""
            isInvalid={false}
            onSelectionChange={(key: any) =>
              dispatch(setSelectedLock(Number(key)))
            }
            selectedKey={selectedLockId ? String(selectedLockId) : undefined}
            children={<></>}
          />
          <Links
            links={skudLinks(location.pathname, !!selectedLockId, handleUnlock)}
          />
        </div>
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4 w-full">
          <div className="flex flex-row justify-between w-full items-center mb-4">
            <div className="flex flex-row gap-4 items-center">
              <span className="font-medium">Карты доступа</span>
            </div>
            <div className="flex flex-row gap-4 items-center">
              <Button
                startContent={<FaPlus />}
                color="primary"
                size="sm"
                variant="solid"
                className="max-w-fit"
                onPress={() => createAccessCardModalDisclosure.onOpen()}>
                Создать
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-2 mb-2">
            <TableBuilder
              displayOptions={false}
              removeWrapper={true}
              columns={[
                {
                  label: "Пользователь",
                  key: "user_id",
                  type: ColumnType.Custom,
                  render: (_value: any, row: AccessCard) => (
                    <UserChip userId={row.user_id} />
                  ),
                },
                {
                  label: "UID карты",
                  key: "card_uid",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  key: "actions",
                  label: "Действия",
                  type: ColumnType.Custom,
                  render: (_value: any, row: AccessCard) => (
                    <div className="flex flex-row gap-2 items-center">
                      <Button
                        size="sm"
                        variant="flat"
                        color="danger"
                        onClick={() => handleDelete(row.id)}>
                        <FaTrash />
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={accessCards}
              rowsPerPage={10}
              defaultSortDescriptor={{
                column: "card_uid",
                direction: "ascending",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
